import { LitElement as S, html as c, nothing as C, css as A } from "lit";
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const _ = (s) => (t, e) => {
  e !== void 0 ? e.addInitializer(() => {
    customElements.define(s, t);
  }) : customElements.define(s, t);
};
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const P = globalThis, U = P.ShadowRoot && (P.ShadyCSS === void 0 || P.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, q = Symbol(), B = /* @__PURE__ */ new WeakMap();
let J = class {
  constructor(t, e, i) {
    if (this._$cssResult$ = !0, i !== q) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (U && t === void 0) {
      const i = e !== void 0 && e.length === 1;
      i && (t = B.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), i && B.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const V = (s) => new J(typeof s == "string" ? s : s + "", void 0, q), W = (s, t) => {
  if (U) s.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else for (const e of t) {
    const i = document.createElement("style"), a = P.litNonce;
    a !== void 0 && i.setAttribute("nonce", a), i.textContent = e.cssText, s.appendChild(i);
  }
}, j = U ? (s) => s : (s) => s instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const i of t.cssRules) e += i.cssText;
  return V(e);
})(s) : s;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: X, defineProperty: H, getOwnPropertyDescriptor: K, getOwnPropertyNames: Q, getOwnPropertySymbols: G, getPrototypeOf: Z } = Object, v = globalThis, F = v.trustedTypes, tt = F ? F.emptyScript : "", z = v.reactiveElementPolyfillSupport, k = (s, t) => s, T = { toAttribute(s, t) {
  switch (t) {
    case Boolean:
      s = s ? tt : null;
      break;
    case Object:
    case Array:
      s = s == null ? s : JSON.stringify(s);
  }
  return s;
}, fromAttribute(s, t) {
  let e = s;
  switch (t) {
    case Boolean:
      e = s !== null;
      break;
    case Number:
      e = s === null ? null : Number(s);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(s);
      } catch {
        e = null;
      }
  }
  return e;
} }, R = (s, t) => !X(s, t), I = { attribute: !0, type: String, converter: T, reflect: !1, useDefault: !1, hasChanged: R };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), v.litPropertyMetadata ?? (v.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class $ extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ?? (this.l = [])).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = I) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const i = Symbol(), a = this.getPropertyDescriptor(t, i, e);
      a !== void 0 && H(this.prototype, t, a);
    }
  }
  static getPropertyDescriptor(t, e, i) {
    const { get: a, set: r } = K(this.prototype, t) ?? { get() {
      return this[e];
    }, set(o) {
      this[e] = o;
    } };
    return { get: a, set(o) {
      const l = a == null ? void 0 : a.call(this);
      r == null || r.call(this, o), this.requestUpdate(t, l, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? I;
  }
  static _$Ei() {
    if (this.hasOwnProperty(k("elementProperties"))) return;
    const t = Z(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(k("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(k("properties"))) {
      const e = this.properties, i = [...Q(e), ...G(e)];
      for (const a of i) this.createProperty(a, e[a]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [i, a] of e) this.elementProperties.set(i, a);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, i] of this.elementProperties) {
      const a = this._$Eu(e, i);
      a !== void 0 && this._$Eh.set(a, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const i = new Set(t.flat(1 / 0).reverse());
      for (const a of i) e.unshift(j(a));
    } else t !== void 0 && e.push(j(t));
    return e;
  }
  static _$Eu(t, e) {
    const i = e.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var t;
    this._$ES = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (t = this.constructor.l) == null || t.forEach((e) => e(this));
  }
  addController(t) {
    var e;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(t), this.renderRoot !== void 0 && this.isConnected && ((e = t.hostConnected) == null || e.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$EO) == null || e.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const i of e.keys()) this.hasOwnProperty(i) && (t.set(i, this[i]), delete this[i]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return W(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var t;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$EO) == null || t.forEach((e) => {
      var i;
      return (i = e.hostConnected) == null ? void 0 : i.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$EO) == null || t.forEach((e) => {
      var i;
      return (i = e.hostDisconnected) == null ? void 0 : i.call(e);
    });
  }
  attributeChangedCallback(t, e, i) {
    this._$AK(t, i);
  }
  _$ET(t, e) {
    var r;
    const i = this.constructor.elementProperties.get(t), a = this.constructor._$Eu(t, i);
    if (a !== void 0 && i.reflect === !0) {
      const o = (((r = i.converter) == null ? void 0 : r.toAttribute) !== void 0 ? i.converter : T).toAttribute(e, i.type);
      this._$Em = t, o == null ? this.removeAttribute(a) : this.setAttribute(a, o), this._$Em = null;
    }
  }
  _$AK(t, e) {
    var r, o;
    const i = this.constructor, a = i._$Eh.get(t);
    if (a !== void 0 && this._$Em !== a) {
      const l = i.getPropertyOptions(a), b = typeof l.converter == "function" ? { fromAttribute: l.converter } : ((r = l.converter) == null ? void 0 : r.fromAttribute) !== void 0 ? l.converter : T;
      this._$Em = a;
      const O = b.fromAttribute(e, l.type);
      this[a] = O ?? ((o = this._$Ej) == null ? void 0 : o.get(a)) ?? O, this._$Em = null;
    }
  }
  requestUpdate(t, e, i) {
    var a;
    if (t !== void 0) {
      const r = this.constructor, o = this[t];
      if (i ?? (i = r.getPropertyOptions(t)), !((i.hasChanged ?? R)(o, e) || i.useDefault && i.reflect && o === ((a = this._$Ej) == null ? void 0 : a.get(t)) && !this.hasAttribute(r._$Eu(t, i)))) return;
      this.C(t, e, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: i, reflect: a, wrapped: r }, o) {
    i && !(this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Map())).has(t) && (this._$Ej.set(t, o ?? e ?? this[t]), r !== !0 || o !== void 0) || (this._$AL.has(t) || (this.hasUpdated || i || (e = void 0), this._$AL.set(t, e)), a === !0 && this._$Em !== t && (this._$Eq ?? (this._$Eq = /* @__PURE__ */ new Set())).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var i;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [r, o] of this._$Ep) this[r] = o;
        this._$Ep = void 0;
      }
      const a = this.constructor.elementProperties;
      if (a.size > 0) for (const [r, o] of a) {
        const { wrapped: l } = o, b = this[r];
        l !== !0 || this._$AL.has(r) || b === void 0 || this.C(r, void 0, o, b);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), (i = this._$EO) == null || i.forEach((a) => {
        var r;
        return (r = a.hostUpdate) == null ? void 0 : r.call(a);
      }), this.update(e)) : this._$EM();
    } catch (a) {
      throw t = !1, this._$EM(), a;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$EO) == null || e.forEach((i) => {
      var a;
      return (a = i.hostUpdated) == null ? void 0 : a.call(i);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq && (this._$Eq = this._$Eq.forEach((e) => this._$ET(e, this[e]))), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
}
$.elementStyles = [], $.shadowRootOptions = { mode: "open" }, $[k("elementProperties")] = /* @__PURE__ */ new Map(), $[k("finalized")] = /* @__PURE__ */ new Map(), z == null || z({ ReactiveElement: $ }), (v.reactiveElementVersions ?? (v.reactiveElementVersions = [])).push("2.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const et = { attribute: !0, type: String, converter: T, reflect: !1, hasChanged: R }, st = (s = et, t, e) => {
  const { kind: i, metadata: a } = e;
  let r = globalThis.litPropertyMetadata.get(a);
  if (r === void 0 && globalThis.litPropertyMetadata.set(a, r = /* @__PURE__ */ new Map()), i === "setter" && ((s = Object.create(s)).wrapped = !0), r.set(e.name, s), i === "accessor") {
    const { name: o } = e;
    return { set(l) {
      const b = t.get.call(this);
      t.set.call(this, l), this.requestUpdate(o, b, s);
    }, init(l) {
      return l !== void 0 && this.C(o, void 0, s, l), l;
    } };
  }
  if (i === "setter") {
    const { name: o } = e;
    return function(l) {
      const b = this[o];
      t.call(this, l), this.requestUpdate(o, b, s);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function n(s) {
  return (t, e) => typeof e == "object" ? st(s, t, e) : ((i, a, r) => {
    const o = a.hasOwnProperty(r);
    return a.constructor.createProperty(r, i), o ? Object.getOwnPropertyDescriptor(a, r) : void 0;
  })(s, t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function g(s) {
  return n({ ...s, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const it = (s, t, e) => (e.configurable = !0, e.enumerable = !0, Reflect.decorate && typeof t != "object" && Object.defineProperty(s, t, e), e);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function at(s, t) {
  return (e, i, a) => {
    const r = (o) => {
      var l;
      return ((l = o.renderRoot) == null ? void 0 : l.querySelector(s)) ?? null;
    };
    return it(e, i, { get() {
      return r(this);
    } });
  };
}
var rt = Object.defineProperty, ot = Object.getOwnPropertyDescriptor, d = (s, t, e, i) => {
  for (var a = i > 1 ? void 0 : i ? ot(t, e) : t, r = s.length - 1, o; r >= 0; r--)
    (o = s[r]) && (a = (i ? o(t, e, a) : o(a)) || a);
  return i && a && rt(t, e, a), a;
};
const N = {
  es: {
    weekdays: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
    weekdaysShort: ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sa"],
    months: [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre"
    ],
    monthsShort: [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic"
    ],
    today: "Hoy",
    cancel: "Cancelar",
    accept: "Aceptar"
  },
  en: {
    weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    weekdaysShort: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    months: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    today: "Today",
    cancel: "Cancel",
    accept: "Accept"
  }
};
let h = class extends S {
  constructor() {
    super(...arguments), this.variant = "single", this.size = "medium", this.locale = "es", this.showFooter = !0, this.showDouble = !1, this.inline = !1, this.currentMonth = (/* @__PURE__ */ new Date()).getMonth(), this.currentYear = (/* @__PURE__ */ new Date()).getFullYear(), this.secondMonth = (/* @__PURE__ */ new Date()).getMonth() + 1, this.secondYear = (/* @__PURE__ */ new Date()).getFullYear(), this.internalSelectedDates = [];
  }
  // ============================================================================
  // Lifecycle
  // ============================================================================
  connectedCallback() {
    super.connectedCallback(), this.initializeDates(), this.updateSecondMonth();
  }
  updated(s) {
    if (super.updated(s), s.has("selectedDate") && this.selectedDate) {
      const t = this.parseDate(this.selectedDate);
      t && (this.internalSelectedDates = [t], this.currentMonth = t.getUTCMonth(), this.currentYear = t.getUTCFullYear());
    }
    if (s.has("selectedDates") && this.selectedDates && (this.internalSelectedDates = this.selectedDates.split(",").map((t) => this.parseDate(t.trim())).filter((t) => t !== null)), s.has("rangeStart") && this.rangeStart) {
      const t = this.parseDate(this.rangeStart);
      this.internalRangeStart = t ?? void 0;
    }
    if (s.has("rangeEnd") && this.rangeEnd) {
      const t = this.parseDate(this.rangeEnd);
      this.internalRangeEnd = t ?? void 0;
    }
  }
  // ============================================================================
  // Styles (Empty - uses external CSS)
  // ============================================================================
  createRenderRoot() {
    return this;
  }
  // ============================================================================
  // Private Methods - Date Utilities
  // ============================================================================
  parseDate(s) {
    if (!s) return null;
    const t = s.split("-");
    if (t.length !== 3) return null;
    const e = parseInt(t[0], 10), i = parseInt(t[1], 10) - 1, a = parseInt(t[2], 10);
    if (isNaN(e) || isNaN(i) || isNaN(a)) return null;
    const r = new Date(Date.UTC(e, i, a, 12, 0, 0));
    return isNaN(r.getTime()) ? null : r;
  }
  formatDate(s) {
    return s.toISOString().split("T")[0];
  }
  isSameDay(s, t) {
    return !s || !t ? !1 : s.getUTCDate() === t.getUTCDate() && s.getUTCMonth() === t.getUTCMonth() && s.getUTCFullYear() === t.getUTCFullYear();
  }
  isToday(s) {
    const t = /* @__PURE__ */ new Date(), e = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate(), 12, 0, 0));
    return this.isSameDay(s, e);
  }
  isDateInRange(s, t, e) {
    if (!t || !e) return !1;
    const i = s.getTime();
    return i > t.getTime() && i < e.getTime();
  }
  isDateDisabled(s) {
    if (this.minDate) {
      const t = this.parseDate(this.minDate);
      if (t && s < t) return !0;
    }
    if (this.maxDate) {
      const t = this.parseDate(this.maxDate);
      if (t && s > t) return !0;
    }
    if (this.disabledDates) {
      const t = this.disabledDates.split(",").map((i) => i.trim()), e = this.formatDate(s);
      if (t.includes(e)) return !0;
    }
    return !1;
  }
  initializeDates() {
    if (this.selectedDate) {
      const s = this.parseDate(this.selectedDate);
      s && (this.internalSelectedDates = [s], this.currentMonth = s.getUTCMonth(), this.currentYear = s.getUTCFullYear());
    }
    if (this.selectedDates && (this.internalSelectedDates = this.selectedDates.split(",").map((s) => this.parseDate(s.trim())).filter((s) => s !== null)), this.rangeStart) {
      const s = this.parseDate(this.rangeStart);
      this.internalRangeStart = s ?? void 0;
    }
    if (this.rangeEnd) {
      const s = this.parseDate(this.rangeEnd);
      this.internalRangeEnd = s ?? void 0;
    }
  }
  updateSecondMonth() {
    this.secondMonth = this.currentMonth + 1, this.secondYear = this.currentYear, this.secondMonth > 11 && (this.secondMonth = 0, this.secondYear++);
  }
  getDaysInMonth(s, t) {
    return new Date(t, s + 1, 0).getDate();
  }
  getFirstDayOfMonth(s, t) {
    return new Date(t, s, 1).getDay();
  }
  generateCalendarDays(s, t) {
    const e = this.getDaysInMonth(s, t), i = this.getFirstDayOfMonth(s, t), a = this.getDaysInMonth(s - 1, t), r = [];
    for (let l = i - 1; l >= 0; l--) {
      const b = a - l, O = new Date(Date.UTC(t, s - 1, b, 12, 0, 0));
      r.push(this.createDateInfo(O, !0));
    }
    for (let l = 1; l <= e; l++) {
      const b = new Date(Date.UTC(t, s, l, 12, 0, 0));
      r.push(this.createDateInfo(b, !1));
    }
    const o = 7 - r.length % 7;
    if (o < 7)
      for (let l = 1; l <= o; l++) {
        const b = new Date(Date.UTC(t, s + 1, l, 12, 0, 0));
        r.push(this.createDateInfo(b, !0));
      }
    return r;
  }
  createDateInfo(s, t) {
    const e = this.internalSelectedDates.some((o) => this.isSameDay(o, s)), i = this.isSameDay(s, this.internalRangeStart), a = this.isSameDay(s, this.internalRangeEnd), r = this.isDateInRange(s, this.internalRangeStart, this.internalRangeEnd);
    return {
      date: s,
      day: s.getUTCDate(),
      month: s.getUTCMonth(),
      year: s.getUTCFullYear(),
      isToday: this.isToday(s),
      isSelected: e,
      isInRange: r,
      isRangeStart: i,
      isRangeEnd: a,
      isOutsideMonth: t,
      isDisabled: this.isDateDisabled(s)
    };
  }
  // ============================================================================
  // Event Handlers
  // ============================================================================
  handlePrevMonth() {
    this.currentMonth--, this.currentMonth < 0 && (this.currentMonth = 11, this.currentYear--), this.updateSecondMonth(), this.dispatchMonthChangeEvent();
  }
  handleNextMonth() {
    this.currentMonth++, this.currentMonth > 11 && (this.currentMonth = 0, this.currentYear++), this.updateSecondMonth(), this.dispatchMonthChangeEvent();
  }
  handleMonthChange(s) {
    const t = s.target;
    this.currentMonth = parseInt(t.value, 10), this.updateSecondMonth(), this.dispatchMonthChangeEvent();
  }
  handleYearChange(s) {
    const t = s.target;
    this.currentYear = parseInt(t.value, 10), this.updateSecondMonth(), this.dispatchMonthChangeEvent();
  }
  handleDayClick(s) {
    if (s.isDisabled || s.isOutsideMonth) return;
    const t = s.date;
    switch (this.variant) {
      case "single":
        this.internalSelectedDates = [t], this.dispatchDateSelectEvent(t), this.dispatchDateChangeEvent();
        break;
      case "multiple":
        const e = this.internalSelectedDates.findIndex((i) => this.isSameDay(i, t));
        e >= 0 ? this.internalSelectedDates.splice(e, 1) : this.internalSelectedDates.push(t), this.internalSelectedDates = [...this.internalSelectedDates], this.dispatchDateSelectEvent(t), this.dispatchDateChangeEvent();
        break;
      case "range":
        !this.internalRangeStart || this.internalRangeStart && this.internalRangeEnd ? (this.internalRangeStart = t, this.internalRangeEnd = void 0) : (t < this.internalRangeStart ? (this.internalRangeEnd = this.internalRangeStart, this.internalRangeStart = t) : this.internalRangeEnd = t, this.dispatchDateChangeEvent()), this.requestUpdate();
        break;
    }
  }
  handleKeyDown(s, t, e) {
    const i = this.generateCalendarDays(this.currentMonth, this.currentYear);
    switch (s.key) {
      case "Enter":
      case " ":
        s.preventDefault(), this.handleDayClick(t);
        break;
      case "ArrowLeft":
        s.preventDefault(), this.focusPreviousDay(e, i);
        break;
      case "ArrowRight":
        s.preventDefault(), this.focusNextDay(e, i);
        break;
      case "ArrowUp":
        s.preventDefault(), this.focusDayAbove(e, i);
        break;
      case "ArrowDown":
        s.preventDefault(), this.focusDayBelow(e, i);
        break;
      case "Home":
        s.preventDefault(), this.focusFirstDay(i);
        break;
      case "End":
        s.preventDefault(), this.focusLastDay(i);
        break;
    }
  }
  focusPreviousDay(s, t) {
    let e = s - 1;
    for (; e >= 0 && (t[e].isDisabled || t[e].isOutsideMonth); )
      e--;
    e >= 0 && this.focusDayButton(e);
  }
  focusNextDay(s, t) {
    let e = s + 1;
    for (; e < t.length && (t[e].isDisabled || t[e].isOutsideMonth); )
      e++;
    e < t.length && this.focusDayButton(e);
  }
  focusDayAbove(s, t) {
    const e = s - 7;
    e >= 0 && !t[e].isDisabled && !t[e].isOutsideMonth && this.focusDayButton(e);
  }
  focusDayBelow(s, t) {
    const e = s + 7;
    e < t.length && !t[e].isDisabled && !t[e].isOutsideMonth && this.focusDayButton(e);
  }
  focusFirstDay(s) {
    const t = s.findIndex((e) => !e.isDisabled && !e.isOutsideMonth);
    t >= 0 && this.focusDayButton(t);
  }
  focusLastDay(s) {
    const t = s.slice().reverse().findIndex((e) => !e.isDisabled && !e.isOutsideMonth);
    if (t >= 0) {
      const e = s.length - 1 - t;
      this.focusDayButton(e);
    }
  }
  focusDayButton(s) {
    this.updateComplete.then(() => {
      var e;
      const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(
        `.sb-ui-calendar__day:nth-child(${s + 1})`
      );
      t == null || t.focus();
    });
  }
  handleCancel() {
    this.dispatchEvent(
      new CustomEvent("calendar-cancel", {
        bubbles: !0,
        composed: !0
      })
    );
  }
  handleAccept() {
    this.dispatchEvent(
      new CustomEvent("calendar-accept", {
        bubbles: !0,
        composed: !0,
        detail: this.getSelectionData()
      })
    );
  }
  // ============================================================================
  // Custom Events
  // ============================================================================
  dispatchDateSelectEvent(s) {
    this.dispatchEvent(
      new CustomEvent("date-select", {
        detail: {
          date: this.formatDate(s),
          timestamp: s.getTime()
        },
        bubbles: !0,
        composed: !0
      })
    );
  }
  dispatchDateChangeEvent() {
    this.dispatchEvent(
      new CustomEvent("date-change", {
        detail: this.getSelectionData(),
        bubbles: !0,
        composed: !0
      })
    );
  }
  dispatchMonthChangeEvent() {
    this.dispatchEvent(
      new CustomEvent("month-change", {
        detail: {
          month: this.currentMonth,
          year: this.currentYear,
          monthName: this.getLocaleConfig().months[this.currentMonth]
        },
        bubbles: !0,
        composed: !0
      })
    );
  }
  getSelectionData() {
    switch (this.variant) {
      case "single":
        return {
          variant: "single",
          date: this.internalSelectedDates[0] ? this.formatDate(this.internalSelectedDates[0]) : null
        };
      case "multiple":
        return {
          variant: "multiple",
          dates: this.internalSelectedDates.map((s) => this.formatDate(s))
        };
      case "range":
        return {
          variant: "range",
          start: this.internalRangeStart ? this.formatDate(this.internalRangeStart) : null,
          end: this.internalRangeEnd ? this.formatDate(this.internalRangeEnd) : null
        };
      default:
        return {};
    }
  }
  // ============================================================================
  // Helpers
  // ============================================================================
  getLocaleConfig() {
    return N[this.locale] || N.es;
  }
  getYearRange() {
    const s = (/* @__PURE__ */ new Date()).getFullYear(), t = s - 100, e = s + 50, i = [];
    for (let a = t; a <= e; a++)
      i.push(a);
    return i;
  }
  getDayClasses(s) {
    const t = ["sb-ui-calendar__day"];
    return s.isToday && t.push("sb-ui-calendar__day--today"), s.isSelected && t.push("sb-ui-calendar__day--selected"), s.isRangeStart && t.push("sb-ui-calendar__day--range-start"), s.isRangeEnd && t.push("sb-ui-calendar__day--range-end"), s.isInRange && t.push("sb-ui-calendar__day--in-range"), s.isOutsideMonth && t.push("sb-ui-calendar__day--outside"), s.isDisabled && t.push("sb-ui-calendar__day--disabled"), t.join(" ");
  }
  getAriaLabel(s) {
    const t = this.getLocaleConfig(), e = t.months[s.month];
    let i = `${s.day} de ${e}, ${s.year}`;
    return s.isToday && (i += `, ${t.today}`), s.isSelected && (i += ", Seleccionado"), s.isRangeStart && (i += ", Inicio del rango"), s.isRangeEnd && (i += ", Fin del rango"), i;
  }
  // ============================================================================
  // Render Methods
  // ============================================================================
  renderNavIcon(s) {
    return s === "left" ? c`
        <svg class="sb-ui-calendar__nav-icon" viewBox="0 0 24 24">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      ` : c`
      <svg class="sb-ui-calendar__nav-icon" viewBox="0 0 24 24">
        <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
      </svg>
    `;
  }
  renderMonth(s, t) {
    const e = this.getLocaleConfig(), i = this.generateCalendarDays(s, t), a = this.getYearRange();
    return c`
      <div class="sb-ui-calendar__month">
        <div class="sb-ui-calendar__header">
          <button
            class="sb-ui-calendar__nav-button"
            @click=${this.handlePrevMonth}
            aria-label="Mes anterior"
          >
            ${this.renderNavIcon("left")}
          </button>
          <div class="sb-ui-calendar__month-year">
            <select
              class="sb-ui-select sb-ui-calendar__month-select"
              .value=${s.toString()}
              @change=${this.handleMonthChange}
              aria-label="Seleccionar mes"
            >
              ${e.months.map(
      (r, o) => c`<option value=${o} ?selected=${o === s}>${r}</option>`
    )}
            </select>
            <select
              class="sb-ui-select sb-ui-calendar__year-select"
              .value=${t.toString()}
              @change=${this.handleYearChange}
              aria-label="Seleccionar año"
            >
              ${a.map(
      (r) => c`<option value=${r} ?selected=${r === t}>${r}</option>`
    )}
            </select>
          </div>
          <button
            class="sb-ui-calendar__nav-button"
            @click=${this.handleNextMonth}
            aria-label="Mes siguiente"
          >
            ${this.renderNavIcon("right")}
          </button>
        </div>

        <div class="sb-ui-calendar__weekdays">
          ${e.weekdaysShort.map(
      (r) => c`<div class="sb-ui-calendar__weekday">${r}</div>`
    )}
        </div>

        <div class="sb-ui-calendar__days">
          ${i.map(
      (r, o) => c`
              <button
                class=${this.getDayClasses(r)}
                @click=${() => this.handleDayClick(r)}
                @keydown=${(l) => this.handleKeyDown(l, r, o)}
                ?disabled=${r.isDisabled || r.isOutsideMonth}
                aria-label=${this.getAriaLabel(r)}
                aria-selected=${r.isSelected}
                tabindex=${r.isSelected || o === 0 && !this.internalSelectedDates.length ? "0" : "-1"}
              >
                ${r.day}
              </button>
            `
    )}
        </div>
      </div>
    `;
  }
  renderFooter() {
    if (!this.showFooter) return C;
    const s = this.getLocaleConfig();
    return c`
      <div class="sb-ui-calendar__footer">
        <button
          class="sb-ui-button sb-ui-button--secondary sb-ui-button--small"
          @click=${this.handleCancel}
        >
          ${s.cancel}
        </button>
        <button
          class="sb-ui-button sb-ui-button--primary sb-ui-button--fill sb-ui-button--small"
          @click=${this.handleAccept}
        >
          ${s.accept}
        </button>
      </div>
    `;
  }
  render() {
    const s = this.size !== "medium" ? `sb-ui-calendar--${this.size}` : "", t = this.showDouble && this.variant === "range" ? "sb-ui-calendar--double" : "", e = this.inline ? "sb-ui-calendar--inline" : "", i = this.showFooter ? "" : "sb-ui-calendar--no-footer", a = ["sb-ui-calendar", s, t, e, i].filter(Boolean).join(" ");
    return c`
      <div class=${a} data-variant=${this.variant}>
        ${this.renderMonth(this.currentMonth, this.currentYear)}
        ${this.showDouble && this.variant === "range" ? this.renderMonth(this.secondMonth, this.secondYear) : C}
        ${this.renderFooter()}
      </div>
    `;
  }
};
d([
  n({ type: String, reflect: !0 })
], h.prototype, "variant", 2);
d([
  n({ type: String, reflect: !0 })
], h.prototype, "size", 2);
d([
  n({ type: String, attribute: "selected-date" })
], h.prototype, "selectedDate", 2);
d([
  n({ type: String, attribute: "selected-dates" })
], h.prototype, "selectedDates", 2);
d([
  n({ type: String, attribute: "range-start" })
], h.prototype, "rangeStart", 2);
d([
  n({ type: String, attribute: "range-end" })
], h.prototype, "rangeEnd", 2);
d([
  n({ type: String, attribute: "min-date" })
], h.prototype, "minDate", 2);
d([
  n({ type: String, attribute: "max-date" })
], h.prototype, "maxDate", 2);
d([
  n({ type: String, attribute: "disabled-dates" })
], h.prototype, "disabledDates", 2);
d([
  n({ type: String })
], h.prototype, "locale", 2);
d([
  n({ type: Boolean, attribute: "show-footer" })
], h.prototype, "showFooter", 2);
d([
  n({ type: Boolean, attribute: "show-double" })
], h.prototype, "showDouble", 2);
d([
  n({ type: Boolean, reflect: !0 })
], h.prototype, "inline", 2);
d([
  g()
], h.prototype, "currentMonth", 2);
d([
  g()
], h.prototype, "currentYear", 2);
d([
  g()
], h.prototype, "secondMonth", 2);
d([
  g()
], h.prototype, "secondYear", 2);
d([
  g()
], h.prototype, "internalSelectedDates", 2);
d([
  g()
], h.prototype, "internalRangeStart", 2);
d([
  g()
], h.prototype, "internalRangeEnd", 2);
h = d([
  _("sb-ui-calendar")
], h);
var nt = Object.defineProperty, lt = Object.getOwnPropertyDescriptor, p = (s, t, e, i) => {
  for (var a = i > 1 ? void 0 : i ? lt(t, e) : t, r = s.length - 1, o; r >= 0; r--)
    (o = s[r]) && (a = (i ? o(t, e, a) : o(a)) || a);
  return i && a && nt(t, e, a), a;
};
let u = class extends S {
  constructor() {
    super(...arguments), this.variant = "single", this.size = "medium", this.placeholder = "DD/MM/YYYY", this.error = !1, this.disabled = !1, this.readonly = !1, this.locale = "es", this.block = !1, this.isOpen = !1, this.handleOutsideClick = (s) => {
      this.contains(s.target) || (this.isOpen = !1);
    };
  }
  // ============================================================================
  // Styles (Empty - uses external CSS)
  // ============================================================================
  createRenderRoot() {
    return this;
  }
  // ============================================================================
  // Lifecycle
  // ============================================================================
  connectedCallback() {
    super.connectedCallback(), document.addEventListener("click", this.handleOutsideClick);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), document.removeEventListener("click", this.handleOutsideClick);
  }
  // ============================================================================
  // Event Handlers
  // ============================================================================
  handleInputClick(s) {
    s.stopPropagation(), !this.disabled && !this.readonly && this.toggleDropdown();
  }
  handleTriggerClick(s) {
    s.stopPropagation(), this.disabled || this.toggleDropdown();
  }
  toggleDropdown() {
    this.isOpen = !this.isOpen, this.isOpen ? this.dispatchEvent(
      new CustomEvent("datepicker-open", {
        bubbles: !0,
        composed: !0
      })
    ) : this.dispatchEvent(
      new CustomEvent("datepicker-close", {
        bubbles: !0,
        composed: !0
      })
    );
  }
  handleCalendarChange(s) {
    var i, a;
    const t = s.detail;
    let e = "";
    this.variant === "single" ? (this.value = t.date, e = this.formatDate(t.date), this.updateInputValue(e), setTimeout(() => {
      this.isOpen = !1;
    }, 300)) : this.variant === "range" ? t.start && t.end && (this.value = `${t.start},${t.end}`, e = `${this.formatDate(t.start)} - ${this.formatDate(t.end)}`, this.updateInputValue(e), setTimeout(() => {
      this.isOpen = !1;
    }, 300)) : this.variant === "multiple" && (this.value = (i = t.dates) == null ? void 0 : i.join(","), e = (a = t.dates) == null ? void 0 : a.map((r) => this.formatDate(r)).join(", "), this.updateInputValue(e)), this.dispatchEvent(
      new CustomEvent("datepicker-change", {
        detail: {
          value: this.value,
          formattedValue: e,
          ...t
        },
        bubbles: !0,
        composed: !0
      })
    );
  }
  handleCalendarAccept(s) {
    this.isOpen = !1, this.dispatchEvent(
      new CustomEvent("datepicker-accept", {
        detail: s.detail,
        bubbles: !0,
        composed: !0
      })
    );
  }
  handleCalendarCancel() {
    this.isOpen = !1;
  }
  updateInputValue(s) {
    this.input && (this.input.value = s);
  }
  // ============================================================================
  // Helpers
  // ============================================================================
  formatDate(s) {
    if (!s) return "";
    const [t, e, i] = s.split("-");
    return `${i}/${e}/${t}`;
  }
  getInitialInputValue() {
    if (!this.value) return "";
    if (this.variant === "single")
      return this.formatDate(this.value);
    if (this.variant === "range") {
      const [s, t] = this.value.split(",");
      return s && t ? `${this.formatDate(s)} - ${this.formatDate(t)}` : s ? this.formatDate(s) : "";
    } else if (this.variant === "multiple")
      return this.value.split(",").map((t) => this.formatDate(t.trim())).join(", ");
    return "";
  }
  // ============================================================================
  // Render
  // ============================================================================
  renderIcon() {
    return c`
      <svg class="sb-ui-datepicker__icon" viewBox="0 0 24 24">
        <path
          d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"
        />
      </svg>
    `;
  }
  render() {
    var t, e;
    const s = [
      "sb-ui-datepicker",
      this.size !== "medium" ? `sb-ui-datepicker--${this.size}` : "",
      this.error ? "sb-ui-datepicker--error" : "",
      this.disabled ? "sb-ui-datepicker--disabled" : "",
      this.block ? "sb-ui-datepicker--block" : "",
      this.isOpen ? "sb-ui-datepicker--open" : "",
      this.label ? "sb-ui-datepicker--with-label" : ""
    ].filter(Boolean).join(" ");
    if (this.variant, this.locale, this.size, this.variant === "single" && this.value)
      this.value;
    else if (this.variant === "range" && this.value) {
      const [i, a] = this.value.split(",");
    } else this.variant === "multiple" && this.value && this.value;
    return this.minDate && this.minDate, this.maxDate && this.maxDate, this.disabledDates && this.disabledDates, c`
      <div class=${s}>
        ${this.label ? c`
              <label class="sb-ui-datepicker__label" style="
                  display: block;
                  font-weight: 600;
                  font-size: 0.875rem;
                  color: var(--sb-ui-color-grayscale-black);
                  margin-bottom: 0.25rem;
                ">
                ${this.label}
              </label>
            ` : C}

        <div class="sb-ui-datepicker__wrapper">
          <input
            type="text"
            class="sb-ui-input sb-ui-datepicker__input ${this.error ? "sb-ui-input--error" : ""}"
            .value=${this.getInitialInputValue()}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled}
            ?readonly=${!0}
            @click=${this.handleInputClick}
          />

          <button
            type="button"
            class="sb-ui-datepicker__trigger"
            ?disabled=${this.disabled}
            @click=${this.handleTriggerClick}
            aria-label="Abrir calendario"
          >
            ${this.renderIcon()}
          </button>

          <div class="sb-ui-datepicker__dropdown">
            <sb-ui-calendar
              .variant=${this.variant}
              .locale=${this.locale}
              .selectedDate=${this.variant === "single" ? this.value : void 0}
              .rangeStart=${this.variant === "range" ? (t = this.value) == null ? void 0 : t.split(",")[0] : void 0}
              .rangeEnd=${this.variant === "range" ? (e = this.value) == null ? void 0 : e.split(",")[1] : void 0}
              .selectedDates=${this.variant === "multiple" ? this.value : void 0}
              .minDate=${this.minDate}
              .maxDate=${this.maxDate}
              .disabledDates=${this.disabledDates}
              .showFooter=${!0}
              .size=${this.size}
              @date-change=${this.handleCalendarChange}
              @calendar-accept=${this.handleCalendarAccept}
              @calendar-cancel=${this.handleCalendarCancel}
            ></sb-ui-calendar>
          </div>
        </div>

        ${this.helperText && !this.error ? c`
              <div
                class="sb-ui-datepicker__helper"
                style="
                  font-size: 0.75rem;
                  color: var(--sb-ui-color-grayscale-base);
                  margin-top: 0.25rem;
                "
              >
                ${this.helperText}
              </div>
            ` : C}
        ${this.error && this.errorMessage ? c`
              <div
                class="sb-ui-datepicker__error"
                style="
                  font-size: 0.75rem;
                  color: var(--sb-ui-color-feedback-error-base);
                  margin-top: 0.25rem;
                "
              >
                ${this.errorMessage}
              </div>
            ` : C}
      </div>
    `;
  }
};
p([
  n({ type: String, reflect: !0 })
], u.prototype, "variant", 2);
p([
  n({ type: String, reflect: !0 })
], u.prototype, "size", 2);
p([
  n({ type: String })
], u.prototype, "value", 2);
p([
  n({ type: String })
], u.prototype, "placeholder", 2);
p([
  n({ type: String })
], u.prototype, "label", 2);
p([
  n({ type: String, attribute: "helper-text" })
], u.prototype, "helperText", 2);
p([
  n({ type: Boolean, reflect: !0 })
], u.prototype, "error", 2);
p([
  n({ type: String, attribute: "error-message" })
], u.prototype, "errorMessage", 2);
p([
  n({ type: Boolean, reflect: !0 })
], u.prototype, "disabled", 2);
p([
  n({ type: Boolean, reflect: !0 })
], u.prototype, "readonly", 2);
p([
  n({ type: String, attribute: "min-date" })
], u.prototype, "minDate", 2);
p([
  n({ type: String, attribute: "max-date" })
], u.prototype, "maxDate", 2);
p([
  n({ type: String, attribute: "disabled-dates" })
], u.prototype, "disabledDates", 2);
p([
  n({ type: String })
], u.prototype, "locale", 2);
p([
  n({ type: Boolean, reflect: !0 })
], u.prototype, "block", 2);
p([
  g()
], u.prototype, "isOpen", 2);
p([
  at(".sb-ui-datepicker__input")
], u.prototype, "input", 2);
u = p([
  _("sb-ui-datepicker")
], u);
var ct = Object.defineProperty, ht = Object.getOwnPropertyDescriptor, Y = (s, t, e, i) => {
  for (var a = i > 1 ? void 0 : i ? ht(t, e) : t, r = s.length - 1, o; r >= 0; r--)
    (o = s[r]) && (a = (i ? o(t, e, a) : o(a)) || a);
  return i && a && ct(t, e, a), a;
};
let E = class extends S {
  constructor() {
    super(...arguments), this.value = "", this.placeholder = "Seleccionar fecha";
  }
  render() {
    return c`
      <div class="date-picker" tabindex="0" role="button" aria-label="Seleccionar fecha">
        ${this.value || this.placeholder}
      </div>
    `;
  }
};
E.styles = A`
    :host {
      display: block;
      --sb-ui-date-picker-border: 1px solid var(--sb-ui-color-grayscale-L200, #e1e1e1);
      --sb-ui-date-picker-border-radius: 8px;
      --sb-ui-date-picker-padding: 0.75rem 1rem;
    }

    .date-picker {
      border: var(--sb-ui-date-picker-border);
      border-radius: var(--sb-ui-date-picker-border-radius);
      padding: var(--sb-ui-date-picker-padding);
      font-family: var(--sb-ui-typography-fontFamily, 'Roboto', sans-serif);
      cursor: pointer;
      background: var(--sb-ui-color-grayscale-white, #ffffff);
    }

    .date-picker:hover {
      border-color: var(--sb-ui-color-primary-base, #007acc);
    }

    .date-picker:focus {
      outline: 2px solid var(--sb-ui-color-primary-base, #007acc);
      outline-offset: 2px;
    }
  `;
Y([
  n({ type: String })
], E.prototype, "value", 2);
Y([
  n({ type: String })
], E.prototype, "placeholder", 2);
E = Y([
  _("sb-ui-date-picker")
], E);
var dt = Object.defineProperty, ut = Object.getOwnPropertyDescriptor, L = (s, t, e, i) => {
  for (var a = i > 1 ? void 0 : i ? ut(t, e) : t, r = s.length - 1, o; r >= 0; r--)
    (o = s[r]) && (a = (i ? o(t, e, a) : o(a)) || a);
  return i && a && dt(t, e, a), a;
};
let x = class extends S {
  constructor() {
    super(...arguments), this.value = "", this.placeholder = "Seleccionar opción";
  }
  render() {
    return c`
      <div class="dropdown" tabindex="0" role="combobox" aria-label="Dropdown">
        ${this.value || this.placeholder}
      </div>
    `;
  }
};
x.styles = A`
    :host {
      display: inline-block;
      --sb-ui-dropdown-border: 1px solid var(--sb-ui-color-grayscale-L200, #e1e1e1);
      --sb-ui-dropdown-border-radius: 8px;
      --sb-ui-dropdown-padding: 0.75rem 1rem;
    }

    .dropdown {
      border: var(--sb-ui-dropdown-border);
      border-radius: var(--sb-ui-dropdown-border-radius);
      padding: var(--sb-ui-dropdown-padding);
      font-family: var(--sb-ui-typography-fontFamily, 'Roboto', sans-serif);
      cursor: pointer;
      background: var(--sb-ui-color-grayscale-white, #ffffff);
      min-width: 200px;
    }

    .dropdown:hover {
      border-color: var(--sb-ui-color-primary-base, #007acc);
    }

    .dropdown:focus {
      outline: 2px solid var(--sb-ui-color-primary-base, #007acc);
      outline-offset: 2px;
    }
  `;
L([
  n({ type: String })
], x.prototype, "value", 2);
L([
  n({ type: String })
], x.prototype, "placeholder", 2);
x = L([
  _("sb-ui-dropdown")
], x);
var pt = Object.defineProperty, bt = Object.getOwnPropertyDescriptor, M = (s, t, e, i) => {
  for (var a = i > 1 ? void 0 : i ? bt(t, e) : t, r = s.length - 1, o; r >= 0; r--)
    (o = s[r]) && (a = (i ? o(t, e, a) : o(a)) || a);
  return i && a && pt(t, e, a), a;
};
let D = class extends S {
  constructor() {
    super(...arguments), this.open = !1, this.title = "", this.closeOnBackdrop = !0, this.showCloseButton = !0;
  }
  connectedCallback() {
    super.connectedCallback(), this.open && this._lockScroll();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._unlockScroll();
  }
  updated(s) {
    s.has("open") && (this.open ? (this._lockScroll(), this._dispatchEvent("sb-ui-modal-open")) : (this._unlockScroll(), this._dispatchEvent("sb-ui-modal-close")));
  }
  _lockScroll() {
    document.body.style.overflow = "hidden";
  }
  _unlockScroll() {
    document.body.style.overflow = "";
  }
  _handleBackdropClick(s) {
    this.closeOnBackdrop && s.target === s.currentTarget && this.close();
  }
  _handleCloseClick() {
    this.close();
  }
  _handleKeyDown(s) {
    s.key === "Escape" && this.open && this.close();
  }
  _dispatchEvent(s, t) {
    this.dispatchEvent(
      new CustomEvent(s, {
        detail: t,
        bubbles: !0,
        composed: !0
      })
    );
  }
  /**
   * Opens the modal
   */
  openModal() {
    this.open = !0;
  }
  /**
   * Closes the modal
   */
  close() {
    this.open = !1;
  }
  render() {
    return this.open ? c`
      <div
        class="backdrop"
        part="backdrop"
        @click=${this._handleBackdropClick}
        @keydown=${this._handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div class="dialog" part="dialog" role="document">
          <div class="header" part="header">
            <h2 id="modal-title" class="title">
              <slot name="header">${this.title}</slot>
            </h2>
            ${this.showCloseButton ? c`
                  <button
                    class="close-button"
                    part="close-button"
                    @click=${this._handleCloseClick}
                    aria-label="Cerrar modal"
                  >
                    ×
                  </button>
                ` : ""}
          </div>

          <div class="body" part="body">
            <slot></slot>
          </div>

          <div class="footer" part="footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    ` : c``;
  }
};
D.styles = A`
    :host {
      --sb-ui-modal-backdrop-bg: rgba(0, 0, 0, 0.5);
      --sb-ui-modal-dialog-bg: var(--sb-ui-color-grayscale-white, #ffffff);
      --sb-ui-modal-dialog-width: 500px;
      --sb-ui-modal-dialog-max-width: 90vw;
      --sb-ui-modal-dialog-border-radius: 12px;
      --sb-ui-modal-dialog-shadow: var(--sb-ui-shadow-xl, 0 20px 25px rgba(0, 0, 0, 0.3));
      --sb-ui-modal-padding: 1.5rem;
      --sb-ui-modal-header-border-bottom: 1px solid var(--sb-ui-color-grayscale-L200, #e1e1e1);
      --sb-ui-modal-footer-border-top: 1px solid var(--sb-ui-color-grayscale-L200, #e1e1e1);
    }

    :host([hidden]) {
      display: none;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      background: var(--sb-ui-modal-backdrop-bg);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
      animation: fadeIn 0.2s ease;
    }

    .dialog {
      background: var(--sb-ui-modal-dialog-bg);
      width: var(--sb-ui-modal-dialog-width);
      max-width: var(--sb-ui-modal-dialog-max-width);
      max-height: 90vh;
      border-radius: var(--sb-ui-modal-dialog-border-radius);
      box-shadow: var(--sb-ui-modal-dialog-shadow);
      display: flex;
      flex-direction: column;
      animation: slideUp 0.3s ease;
    }

    .header {
      padding: var(--sb-ui-modal-padding);
      border-bottom: var(--sb-ui-modal-header-border-bottom);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    .title {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--sb-ui-color-grayscale-D400, #282828);
      margin: 0;
    }

    .close-button {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: var(--sb-ui-color-grayscale-base, #9b9b9b);
      padding: 0.25rem;
      line-height: 1;
      transition: color 0.2s ease;
    }

    .close-button:hover {
      color: var(--sb-ui-color-grayscale-D400, #282828);
    }

    .body {
      padding: var(--sb-ui-modal-padding);
      overflow-y: auto;
      flex: 1;
    }

    .footer {
      padding: var(--sb-ui-modal-padding);
      border-top: var(--sb-ui-modal-footer-border-top);
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideUp {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .backdrop,
      .dialog {
        animation: none;
      }
    }
  `;
M([
  n({ type: Boolean, reflect: !0 })
], D.prototype, "open", 2);
M([
  n({ type: String })
], D.prototype, "title", 2);
M([
  n({ type: Boolean, attribute: "close-on-backdrop" })
], D.prototype, "closeOnBackdrop", 2);
M([
  n({ type: Boolean, attribute: "show-close-button" })
], D.prototype, "showCloseButton", 2);
D = M([
  _("sb-ui-modal")
], D);
class w {
  constructor() {
    this.toasts = /* @__PURE__ */ new Map(), this.containers = /* @__PURE__ */ new Map(), this.counter = 0;
  }
  static getInstance() {
    return w.instance || (w.instance = new w()), w.instance;
  }
  /**
   * Creates and shows a toast
   */
  show(t = {}) {
    const e = `toast-${++this.counter}-${Date.now()}`, i = t.position || "top-right", a = document.createElement("sb-ui-toast");
    t.type && (a.type = t.type), t.title && (a.title = t.title), t.message && (a.message = t.message), t.position && (a.position = t.position), t.size && (a.size = t.size), t.autoDismiss !== void 0 && (a.autoDismiss = t.autoDismiss), t.showClose !== void 0 && (a.showClose = t.showClose), t.showProgress !== void 0 && (a.showProgress = t.showProgress), t.clickable !== void 0 && (a.clickable = t.clickable);
    const r = this.getOrCreateContainer(i);
    return r.appendChild(a), this.toasts.set(e, { id: e, element: a, container: r }), this.setupToastEventListeners(e, a), requestAnimationFrame(() => {
      a.show();
    }), e;
  }
  /**
   * Hides a specific toast by ID
   */
  hide(t) {
    const e = this.toasts.get(t);
    return e ? (e.element.hide(), !0) : !1;
  }
  /**
   * Removes a toast completely
   */
  remove(t) {
    const e = this.toasts.get(t);
    return e ? (e.element.remove(), this.toasts.delete(t), this.cleanupEmptyContainers(), !0) : !1;
  }
  /**
   * Hides all toasts
   */
  hideAll() {
    this.toasts.forEach((t) => {
      t.element.hide();
    });
  }
  /**
   * Removes all toasts
   */
  removeAll() {
    this.toasts.forEach((t) => {
      t.element.remove();
    }), this.toasts.clear(), this.cleanupAllContainers();
  }
  /**
   * Gets all active toast IDs
   */
  getActiveToasts() {
    return Array.from(this.toasts.keys());
  }
  /**
   * Gets toast count by position
   */
  getToastCount(t) {
    return t ? Array.from(this.toasts.values()).filter(
      (e) => e.element.position === t
    ).length : this.toasts.size;
  }
  // Convenience methods for different toast types
  success(t, e = {}) {
    return this.show({ ...e, type: "success", message: t });
  }
  error(t, e = {}) {
    return this.show({ ...e, type: "error", message: t });
  }
  warning(t, e = {}) {
    return this.show({ ...e, type: "warning", message: t });
  }
  info(t, e = {}) {
    return this.show({ ...e, type: "info", message: t });
  }
  getOrCreateContainer(t) {
    let e = this.containers.get(t);
    return e || (e = document.createElement("div"), e.className = `sb-ui-toast-container sb-ui-toast-container--${t}`, e.setAttribute("aria-live", "polite"), e.setAttribute("aria-label", `Notificaciones ${t.replace("-", " ")}`), this.applyContainerStyles(e, t), document.body.appendChild(e), this.containers.set(t, e)), e;
  }
  applyContainerStyles(t, e) {
    switch (t.style.position = "fixed", t.style.zIndex = "1000", t.style.display = "flex", t.style.flexDirection = "column", t.style.gap = "0.5rem", t.style.maxWidth = "400px", t.style.minWidth = "300px", t.style.pointerEvents = "none", e) {
      case "top-right":
        t.style.top = "1rem", t.style.right = "1rem";
        break;
      case "top-left":
        t.style.top = "1rem", t.style.left = "1rem";
        break;
      case "top-center":
        t.style.top = "1rem", t.style.left = "50%", t.style.transform = "translateX(-50%)";
        break;
      case "bottom-right":
        t.style.bottom = "1rem", t.style.right = "1rem", t.style.flexDirection = "column-reverse";
        break;
      case "bottom-left":
        t.style.bottom = "1rem", t.style.left = "1rem", t.style.flexDirection = "column-reverse";
        break;
      case "bottom-center":
        t.style.bottom = "1rem", t.style.left = "50%", t.style.transform = "translateX(-50%)", t.style.flexDirection = "column-reverse";
        break;
    }
    const i = window.matchMedia("(max-width: 640px)");
    i.matches && this.applyMobileStyles(t, e), i.addEventListener("change", (a) => {
      a.matches ? this.applyMobileStyles(t, e) : this.applyDesktopStyles(t, e);
    });
  }
  applyMobileStyles(t, e) {
    t.style.left = "0.5rem", t.style.right = "0.5rem", t.style.maxWidth = "none", t.style.minWidth = "auto", t.style.transform = "none", e.includes("top") ? (t.style.top = "0.5rem", t.style.bottom = "auto") : (t.style.bottom = "0.5rem", t.style.top = "auto");
  }
  applyDesktopStyles(t, e) {
    this.applyContainerStyles(t, e);
  }
  setupToastEventListeners(t, e) {
    e.addEventListener("sb-ui-toast-hide", () => {
      setTimeout(() => {
        this.remove(t);
      }, 300);
    }), e.style.pointerEvents = "auto";
  }
  cleanupEmptyContainers() {
    this.containers.forEach((t, e) => {
      t.children.length === 0 && (t.remove(), this.containers.delete(e));
    });
  }
  cleanupAllContainers() {
    this.containers.forEach((t) => {
      t.remove();
    }), this.containers.clear();
  }
}
const y = w.getInstance(), vt = (s) => y.show(s), Dt = (s, t) => y.success(s, t), wt = (s, t) => y.error(s, t), St = (s, t) => y.warning(s, t), _t = (s, t) => y.info(s, t), $t = (s) => y.hide(s), Ct = (s) => y.remove(s), kt = () => y.hideAll(), Et = () => y.removeAll();
var mt = Object.defineProperty, ft = Object.getOwnPropertyDescriptor, f = (s, t, e, i) => {
  for (var a = i > 1 ? void 0 : i ? ft(t, e) : t, r = s.length - 1, o; r >= 0; r--)
    (o = s[r]) && (a = (i ? o(t, e, a) : o(a)) || a);
  return i && a && mt(t, e, a), a;
};
let m = class extends S {
  constructor() {
    super(...arguments), this.type = "info", this.position = "top-right", this.size = "medium", this.title = "", this.message = "", this.visible = !1, this.showClose = !0, this.autoDismiss = 0, this.showProgress = !1, this.clickable = !0, this._progressWidth = 100;
  }
  connectedCallback() {
    super.connectedCallback(), this.visible && this._setupAutoDismiss();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._clearTimers();
  }
  updated(s) {
    s.has("visible") && (this.visible ? (this._dispatchEvent("sb-ui-toast-show"), this._setupAutoDismiss()) : (this._dispatchEvent("sb-ui-toast-hide"), this._clearTimers())), s.has("autoDismiss") && this.visible && this._setupAutoDismiss();
  }
  _setupAutoDismiss() {
    this._clearTimers(), this.autoDismiss > 0 && (this.showProgress && (this._progressWidth = 100, this.requestUpdate(), setTimeout(() => {
      this._progressWidth = 0, this.requestUpdate();
    }, 50)), this._dismissTimer = window.setTimeout(() => {
      this.hide();
    }, this.autoDismiss));
  }
  _clearTimers() {
    this._dismissTimer && (clearTimeout(this._dismissTimer), this._dismissTimer = void 0), this._progressTimer && (clearTimeout(this._progressTimer), this._progressTimer = void 0);
  }
  _handleClick() {
    this.clickable && this._dispatchEvent("sb-ui-toast-click");
  }
  _handleCloseClick(s) {
    s.stopPropagation(), this.hide();
  }
  _handleKeyDown(s) {
    s.key === "Escape" && this.hide();
  }
  _dispatchEvent(s, t) {
    this.dispatchEvent(
      new CustomEvent(s, {
        detail: t,
        bubbles: !0,
        composed: !0
      })
    );
  }
  /**
   * Shows the toast
   */
  show() {
    this.visible = !0;
  }
  /**
   * Hides the toast
   */
  hide() {
    this.visible = !1;
  }
  /**
   * Toggles the toast visibility
   */
  toggle() {
    this.visible = !this.visible;
  }
  /**
   * Pauses auto-dismiss timer (useful on hover)
   */
  pauseAutoDismiss() {
    this._clearTimers();
  }
  /**
   * Resumes auto-dismiss timer
   */
  resumeAutoDismiss() {
    this.visible && this.autoDismiss > 0 && this._setupAutoDismiss();
  }
  render() {
    const s = this.showProgress && this.autoDismiss > 0 ? `width: ${this._progressWidth}%; transition-duration: ${this.autoDismiss}ms;` : "display: none;";
    return c`
      <div
        class="toast"
        part="toast"
        @click=${this._handleClick}
        @keydown=${this._handleKeyDown}
        @mouseenter=${this.pauseAutoDismiss}
        @mouseleave=${this.resumeAutoDismiss}
        tabindex="0"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="icon" part="icon">
          <slot name="icon"></slot>
        </div>

        <div class="content" part="content">
          ${this.title ? c`
                <div class="title" part="title">
                  <slot name="title">${this.title}</slot>
                </div>
              ` : ""}

          <div class="message" part="message">
            <slot>${this.message}</slot>
          </div>

          <slot name="actions"></slot>
        </div>

        ${this.showClose ? c`
              <button
                class="close"
                part="close"
                @click=${this._handleCloseClick}
                aria-label="Cerrar notificación"
                type="button"
              >
                ×
              </button>
            ` : ""}

        <div class="progress" part="progress" style=${s}></div>
      </div>
    `;
  }
};
m.styles = A`
    :host {
      /* Importar estilos base de alert.css mediante CSS custom properties */
      --sb-ui-toast-bg-color: var(
        --sb-ui-alert-bg-color,
        var(--sb-ui-color-grayscale-white, #ffffff)
      );
      --sb-ui-toast-text-color: var(
        --sb-ui-alert-text-color,
        var(--sb-ui-color-grayscale-D300, #404040)
      );
      --sb-ui-toast-border-color: var(
        --sb-ui-alert-border-color,
        var(--sb-ui-color-grayscale-L200, #edeef0)
      );
      --sb-ui-toast-shadow: var(--sb-ui-alert-shadow-toast, 0 4px 16px rgba(0, 0, 0, 0.15));
      --sb-ui-toast-border-radius: var(--sb-ui-alert-border-radius, 8px);
      --sb-ui-toast-padding: var(--sb-ui-alert-padding, 1rem);
      --sb-ui-toast-gap: var(--sb-ui-alert-gap, 0.75rem);
      --sb-ui-toast-font-family: var(
        --sb-ui-alert-font-family,
        var(--sb-ui-typography-fontFamily, 'Roboto', sans-serif)
      );
      --sb-ui-toast-transition: var(--sb-ui-alert-transition, all 0.2s ease);

      /* Posicionamiento */
      position: fixed;
      z-index: 1000;
      display: block;
      opacity: 0;
      transform: translateX(100%);
      transition: var(--sb-ui-toast-transition);
    }

    :host([visible]) {
      opacity: 1;
      transform: translateX(0);
    }

    :host([position='top-right']) {
      top: 1rem;
      right: 1rem;
    }

    :host([position='top-left']) {
      top: 1rem;
      left: 1rem;
      transform: translateX(-100%);
    }

    :host([position='top-left'][visible]) {
      transform: translateX(0);
    }

    :host([position='top-center']) {
      top: 1rem;
      left: 50%;
      transform: translateX(-50%) translateY(-100%);
    }

    :host([position='top-center'][visible]) {
      transform: translateX(-50%) translateY(0);
    }

    :host([position='bottom-right']) {
      bottom: 1rem;
      right: 1rem;
      transform: translateX(100%) translateY(100%);
    }

    :host([position='bottom-right'][visible]) {
      transform: translateX(0) translateY(0);
    }

    :host([position='bottom-left']) {
      bottom: 1rem;
      left: 1rem;
      transform: translateX(-100%) translateY(100%);
    }

    :host([position='bottom-left'][visible]) {
      transform: translateX(0) translateY(0);
    }

    :host([position='bottom-center']) {
      bottom: 1rem;
      left: 50%;
      transform: translateX(-50%) translateY(100%);
    }

    :host([position='bottom-center'][visible]) {
      transform: translateX(-50%) translateY(0);
    }

    .toast {
      /* Aplicar estilos base de alert.css */
      display: flex;
      align-items: flex-start;
      gap: var(--sb-ui-toast-gap);
      position: relative;
      box-sizing: border-box;
      min-height: 3rem;
      min-width: 300px;
      max-width: 400px;
      padding: var(--sb-ui-toast-padding);
      background-color: var(--sb-ui-toast-bg-color);
      border: 1px solid var(--sb-ui-toast-border-color);
      border-left: 4px solid var(--sb-ui-toast-border-color);
      border-radius: var(--sb-ui-toast-border-radius);
      box-shadow: var(--sb-ui-toast-shadow);
      font-family: var(--sb-ui-toast-font-family);
      line-height: 1.25;
      color: var(--sb-ui-toast-text-color);
      cursor: pointer;
      transition: var(--sb-ui-toast-transition);
    }

    .toast:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      font-size: 1.25rem;
      flex-shrink: 0;
      margin-top: 0.125rem;
    }

    .content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
    }

    .title {
      font-size: 0.875rem;
      font-weight: 600;
      line-height: 1.25;
      margin: 0;
      color: inherit;
    }

    .message {
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.25;
      margin: 0;
      color: inherit;
      opacity: 0.8;
    }

    .close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      background: none;
      border: none;
      color: var(--sb-ui-color-grayscale-base, #d9d9d9);
      font-size: 1.25rem;
      font-weight: 400;
      line-height: 1;
      cursor: pointer;
      border-radius: 4px;
      transition: var(--sb-ui-toast-transition);
      flex-shrink: 0;
      margin-top: -0.125rem;
      margin-right: -0.25rem;
    }

    .close:hover {
      color: var(--sb-ui-color-grayscale-D300, #404040);
      background-color: var(--sb-ui-color-grayscale-L400, rgba(247, 247, 247, 0.5));
    }

    .close:focus-visible {
      outline: 2px solid var(--sb-ui-color-primary-base, #007acc);
      outline-offset: 2px;
    }

    .progress {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
      background-color: var(--sb-ui-color-primary-base, #007acc);
      border-radius: 0 0 var(--sb-ui-toast-border-radius) var(--sb-ui-toast-border-radius);
      transition: width linear;
      opacity: 0.7;
    }

    /* Toast Type Variants */
    :host([type='success']) .toast {
      --sb-ui-toast-bg-color: var(--sb-ui-color-feedback-success-L400, rgba(40, 167, 69, 0.1));
      --sb-ui-toast-border-color: var(--sb-ui-color-feedback-success-base, #28a745);
      --sb-ui-toast-text-color: var(--sb-ui-color-feedback-success-base, #28a745);
    }

    :host([type='success']) .icon::before {
      content: '✓';
      color: var(--sb-ui-color-feedback-success-base, #28a745);
    }

    :host([type='success']) .progress {
      background-color: var(--sb-ui-color-feedback-success-base, #28a745);
    }

    :host([type='info']) .toast {
      --sb-ui-toast-bg-color: var(--sb-ui-color-feedback-info-L400, rgba(0, 122, 204, 0.1));
      --sb-ui-toast-border-color: var(--sb-ui-color-feedback-info-base, #007acc);
      --sb-ui-toast-text-color: var(--sb-ui-color-feedback-info-base, #007acc);
    }

    :host([type='info']) .icon::before {
      content: 'i';
      font-style: italic;
      font-weight: 600;
      color: var(--sb-ui-color-feedback-info-base, #007acc);
    }

    :host([type='info']) .progress {
      background-color: var(--sb-ui-color-feedback-info-base, #007acc);
    }

    :host([type='warning']) .toast {
      --sb-ui-toast-bg-color: var(--sb-ui-color-feedback-warning-L400, rgba(255, 193, 7, 0.1));
      --sb-ui-toast-border-color: var(--sb-ui-color-feedback-warning-base, #ffc107);
      --sb-ui-toast-text-color: var(--sb-ui-color-feedback-warning-base, #ffc107);
    }

    :host([type='warning']) .icon::before {
      content: '⚠';
      color: var(--sb-ui-color-feedback-warning-base, #ffc107);
    }

    :host([type='warning']) .progress {
      background-color: var(--sb-ui-color-feedback-warning-base, #ffc107);
    }

    :host([type='error']) .toast {
      --sb-ui-toast-bg-color: var(--sb-ui-color-feedback-error-L400, rgba(220, 53, 69, 0.1));
      --sb-ui-toast-border-color: var(--sb-ui-color-feedback-error-base, #dc3545);
      --sb-ui-toast-text-color: var(--sb-ui-color-feedback-error-base, #dc3545);
    }

    :host([type='error']) .icon::before {
      content: '✕';
      color: var(--sb-ui-color-feedback-error-base, #dc3545);
    }

    :host([type='error']) .progress {
      background-color: var(--sb-ui-color-feedback-error-base, #dc3545);
    }

    /* Size Variants */
    :host([size='small']) .toast {
      min-height: 2.5rem;
      padding: 0.75rem;
      gap: 0.5rem;
    }

    :host([size='small']) .icon {
      width: 1rem;
      height: 1rem;
      font-size: 1rem;
    }

    :host([size='small']) .title,
    :host([size='small']) .message {
      font-size: 0.8125rem;
    }

    :host([size='small']) .close {
      width: 1.25rem;
      height: 1.25rem;
    }

    :host([size='large']) .toast {
      min-height: 3.5rem;
      padding: 1.25rem;
      gap: 1rem;
    }

    :host([size='large']) .icon {
      width: 1.5rem;
      height: 1.5rem;
      font-size: 1.5rem;
    }

    :host([size='large']) .title {
      font-size: 1rem;
    }

    :host([size='large']) .message {
      font-size: 0.9375rem;
    }

    :host([size='large']) .close {
      width: 1.75rem;
      height: 1.75rem;
    }

    /* Accessibility */
    @media (prefers-reduced-motion: reduce) {
      :host {
        transition: none;
      }

      .toast {
        transition: none;
      }

      .toast:hover {
        transform: none;
      }
    }

    @media (prefers-contrast: high) {
      .toast {
        border-width: 2px;
        border-left-width: 6px;
      }
    }

    /* Mobile Optimization */
    @media (max-width: 640px) {
      :host([position*='top']) {
        top: 0.5rem;
        left: 0.5rem;
        right: 0.5rem;
        transform: translateY(-100%);
      }

      :host([position*='top'][visible]) {
        transform: translateY(0);
      }

      :host([position*='bottom']) {
        bottom: 0.5rem;
        left: 0.5rem;
        right: 0.5rem;
        transform: translateY(100%);
      }

      :host([position*='bottom'][visible]) {
        transform: translateY(0);
      }

      .toast {
        min-width: auto;
        max-width: none;
      }
    }
  `;
f([
  n({ type: String, reflect: !0 })
], m.prototype, "type", 2);
f([
  n({ type: String, reflect: !0 })
], m.prototype, "position", 2);
f([
  n({ type: String, reflect: !0 })
], m.prototype, "size", 2);
f([
  n({ type: String })
], m.prototype, "title", 2);
f([
  n({ type: String })
], m.prototype, "message", 2);
f([
  n({ type: Boolean, reflect: !0 })
], m.prototype, "visible", 2);
f([
  n({ type: Boolean, attribute: "show-close" })
], m.prototype, "showClose", 2);
f([
  n({ type: Number, attribute: "auto-dismiss" })
], m.prototype, "autoDismiss", 2);
f([
  n({ type: Boolean, attribute: "show-progress" })
], m.prototype, "showProgress", 2);
f([
  n({ type: Boolean })
], m.prototype, "clickable", 2);
f([
  g()
], m.prototype, "_progressWidth", 2);
m = f([
  _("sb-ui-toast")
], m);
export {
  h as SbUiCalendar,
  u as SbUiDatePicker,
  E as SbUiDatePickerOld,
  x as SbUiDropdown,
  D as SbUiModal,
  m as SbUiToast,
  w as ToastManager,
  kt as hideAllToasts,
  $t as hideToast,
  Et as removeAllToasts,
  Ct as removeToast,
  wt as showError,
  _t as showInfo,
  Dt as showSuccess,
  vt as showToast,
  St as showWarning
};
